import { ToolbarButton, TableDataRequest } from '@fl/cmsch-fe-library';
import React, { FC, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Opt } from 'ts-opt';
import { StableToChoose } from 'api/gen/StableToChoose';
import { useOurTranslation } from 'app/translations';
import { ConnectedTable } from 'utils/tables';
import { farmStablesColumns } from './columns';
import { simpleAnimalBrowserSelector } from '../../model';
interface Props {
  /** Shows the CSV export button as loading while the export request runs. */
  exportInProgress?: boolean;
  /** Disables the CSV export button when no rows are available or export is blocked. */
  exportDisabled?: boolean;
  /** Fetches the stable table data for the current pagination and filters. */
  getStables(tableDataRequest: Opt<TableDataRequest<StableToChoose>>): void;
  /** Starts the CSV export for the stable overview table. */
  exportToCsv(): void;
}
const pathnamesForRefresh: Array<RegExp> = [/^\/animal-browser\/cow-browser\//, /^\/animal-browser\/calf-browser\//];
const StablesTableBase: FC<Props> = props => {
  const {
    exportInProgress,
    exportDisabled,
    getStables,
    exportToCsv
  } = props;
  const {
    t,
    tCommon
  } = useOurTranslation('animalBrowser');
  const productionAreasInCentralEvidenceOptions = useSelector(simpleAnimalBrowserSelector.productionAreasInCentralEvidenceOptions);
  const headerButtons: Array<ToolbarButton> = useMemo(() => [{
    disabled: exportDisabled,
    onClick: exportToCsv,
    title: tCommon('downloadCsv'),
    id: 'exportCalves',
    role: 'export',
    loading: exportInProgress
  }], [exportDisabled, exportInProgress, exportToCsv, tCommon]);
  return <ConnectedTable tableId="stableToChoose" columns={farmStablesColumns(productionAreasInCentralEvidenceOptions.orElse([]), t)} onFetchTableData={getStables} pathnamesForRefresh={pathnamesForRefresh} rowDataPageSelector={simpleAnimalBrowserSelector.stablePage} enableTableSettings enableTableUserSettings paginationPosition="both" withoutVerticalScrollbar lazyLoadingLimit={20} showFulltextFilter headerButtons={headerButtons} data-sentry-element="ConnectedTable" data-sentry-component="StablesTableBase" data-sentry-source-file="index.tsx" />;
};

/**
 * Renders the stable overview table used as the landing table of the basic animal browser.
 *
 * @param props Stable-fetch callback and CSV export controls.
 * @returns The farms and stables connected table.
 *
 * @example
 * <StablesTable getStables={fetchStables} exportToCsv={handleExport} />
 */
export const StablesTable = memo(StablesTableBase);